import React, { useMemo } from 'react';
import { Box, Collapse, Grid, styled } from '@mui/material';
import { ElementInventoryStatus, ElementType } from '@/redux/slices/slice_types';
import { ElementStatusColorsOrdered, getColorByElementStatus, getElementTypeLabel } from '@/components/_common/Table/Calendar/utils';
import PolarisIcon from '@/components/_common/PolarisIcon';
import ProgressBar from '@/components/_common/Table/Calendar/CalendarSharedComponents/ProgressBar';
import Element, { Props as ElementProps } from '../../CalendarComponents/Element';
import { ReadinessElement } from '@/components/_common/Table/Calendar/Calendar.types';
import { useFocusTheme } from '@/components/Focus/configs/useFocusTheme.hook';
import { FocusTheme } from '@/components/Focus/configs/theme';
import AvailEnumMap from '@/components/Focus/Avails/AvailsEnumMap';

interface Props {
  isOpen: boolean;
  type: ElementType;
  elements: ReadinessElement[];
  statusFilter: ElementInventoryStatus[];
  toggle(): void;
}

type ElementPropsMap = {
  [tier1: string]: {
    [tier2: string | symbol]: ElementProps;
  };
};

const NoVideoResolutionSymbol = Symbol();

const ElementsByTypeAccordion: React.FC<Props> = ({ isOpen, type, elements, statusFilter, toggle }) => {
  const theme = useFocusTheme();

  const { total, completedPercentage } = useMemo(() => {
    const total = elements.length;
    const completed = elements.reduce((acc, el) => {
      if (el.inventory_status === ElementInventoryStatus.Available) acc++;
      return acc;
    }, 0);
    const completedPercentage = Math.floor((completed / total) * 100);
    return {
      total,
      completedPercentage
    };
  }, [elements]);

  const filteredElements = useMemo(() => {
    if (!statusFilter.length) {
      return elements;
    }

    return elements.filter((el) => statusFilter.includes(el.inventory_status));
  }, [elements, statusFilter]);

  // Here we want to group elements by `primaryText` (which is `language`) and `secondaryText` text, whose value would
  // depend on element type (for video elements it's `video_resolution_type`, and `element_subtype` for all the rest types).
  // If two elements have the same `primaryText` and `secondaryText` text we want to display them as one element.
  // If those elements have different `status`, then we display the "worse" status, which is defined by the
  // `ElementStatusColorsOrdered` array (which is in order from "best" to "worse").
  const preparedElementsProps = useMemo((): ElementProps[] => {
    const groupingMap: ElementPropsMap = {};

    return filteredElements.reduce<ElementProps[]>((acc, { inventory_status, platform, language, element_subtype, video_resolution_type }) => {
      const tier1 = language;
      if (!groupingMap[tier1]) groupingMap[tier1] = {};

      const tier2 = type === 'video' ? (video_resolution_type ?? NoVideoResolutionSymbol) : element_subtype;
      if (!groupingMap[tier1][tier2]) {
        groupingMap[tier1][tier2] = {
          platforms: new Set([platform]),
          statusColor: getColorByElementStatus(inventory_status),
          primaryText: (
            <>
              <AvailEnumMap type="language" value={language} /> [{language}]
            </>
          ),
          secondaryText: type === 'video' ? video_resolution_type?.toUpperCase() : element_subtype
        };

        acc.push(groupingMap[tier1][tier2]);
      } else {
        groupingMap[tier1][tier2].platforms.add(platform);

        const existingColor = groupingMap[tier1][tier2].statusColor;
        const existingColorIndex = ElementStatusColorsOrdered.indexOf(existingColor);
        const newColor = getColorByElementStatus(inventory_status);
        const newColorIndex = ElementStatusColorsOrdered.indexOf(newColor);
        if (newColorIndex > existingColorIndex) {
          // update `statusColor` if the new color is "worse".
          groupingMap[tier1][tier2].statusColor = newColor;
        }
      }

      return acc;
    }, []);
  }, [filteredElements]);

  return (
    <Wrapper isOpen={isOpen}>
      <Summary isOpen={isOpen} onClick={toggle}>
        <Grid container sx={{ mb: 1.5 }}>
          <Grid item xs>
            {getElementTypeLabel(type)} {completedPercentage}%
          </Grid>
          <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
            <ArrowIcon icon="down_filled" isRotated={isOpen} width={11} height={11} />
          </Grid>
        </Grid>
        <ProgressBar value={completedPercentage} />
      </Summary>
      <Details in={isOpen}>
        <Box sx={{ mb: 2, color: theme.customOverrides.Calendar.textSecondary }}>
          {total} element{total > 1 ? 's' : null} total
        </Box>
        {preparedElementsProps.map((elementProps, index) => (
          <Element key={index} {...elementProps} />
        ))}
        <Box sx={{ pb: 2 }} />
      </Details>
    </Wrapper>
  );
};

export default ElementsByTypeAccordion;

const Wrapper = styled('div')<{ isOpen: boolean }>`
  ${({ theme, isOpen }) => `
    margin-bottom: ${theme.spacing(0.5)};
    background-color: ${isOpen ? (theme as FocusTheme).customOverrides.Calendar.backgroundDefault : (theme as FocusTheme).customOverrides.Calendar.background};
    border-radius: ${theme.shape.borderRadius}px;
    transition: ${theme.transitions.create('background-color')};
  `}
`;

const Summary = styled('div')<{ isOpen: boolean }>`
  ${({ theme, isOpen }) => `
    padding: ${theme.spacing(2)};
    font-size: 1.143rem;
    color: ${isOpen ? (theme as FocusTheme).customOverrides.Calendar.text : (theme as FocusTheme).customOverrides.Calendar.highlight};
    cursor: pointer;
  `}
`;

const Details = styled(Collapse)`
  ${({ theme }) => `
    padding: ${theme.spacing(0, 2)};
  `}
`;

const ArrowIcon = styled(PolarisIcon, { shouldForwardProp: (propName: string) => !['isRotated'].includes(propName) })<{ isRotated: boolean }>`
  ${({ theme, isRotated }) => `
    ${isRotated ? 'transform: rotate(180deg);' : ''}
    transition: ${theme.transitions.create('transform')};
  `}
`;
